import { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer'
import { fetchNearbyPhotos } from 'features/kestrelViewer/utils/fetchNearbyPhotos'
import omit from 'lodash/omit'
import { KVPhoto } from 'types'

import { KVMessageDef } from './index'

/**
 * This message is sent when the orbit is placed.
 * It is used to score the 2D photos in how the contribute to the 3D point and set the filtered photos in the context.
 * Expects the message to have the X, Y, and Z location of the orbit
 * @returns KVMessageDef
 */
export function getOrbitPlacedMessage() {
  return {
    type: 'orbitPositionResponse',
    requiredData: ['x', 'y', 'z'],

    callback: async (
      json: Record<string, unknown>,
      kv: KestrelViewerMessageCallbackAPI
    ) => {
      const worldPosition = {
        x: json.x as number,
        y: json.y as number,
        z: json.z as number,
      }

      if (!kv.kestrelModel?.id) {
        console.warn('No kestrel model available for nearby photos API')
        return
      }

      const cameraPosesByFilename =
        kv.kestrelModelCameras?.data?.cameraPoses ?? {}
      if (Object.keys(cameraPosesByFilename).length === 0) {
        console.warn('No camera poses available for nearby photos')
        kv.setFilteredPhotos(null)
        return
      }

      try {
        kv.setFilteringPhotos(true)
        const photoResponses = await fetchNearbyPhotos(
          worldPosition,
          cameraPosesByFilename
        )

        // Use functional update to get current photos value (avoids stale closure)
        kv.setPhotos((currentPhotos: KVPhoto[]) => {
          const updatedPhotos = photoResponses
            .map((photoResponse) => {
              const existingPhoto = currentPhotos.find(
                (photo) => photo.filename === photoResponse.image_name
              )
              if (existingPhoto) {
                return {
                  ...existingPhoto,
                  projection: {
                    filename: photoResponse.image_name,
                    x: photoResponse.projection.x,
                    y: photoResponse.projection.y,
                    originalImageWidth: photoResponse.width,
                    originalImageHeight: photoResponse.height,
                  },
                }
              }
              return null
            })
            .filter(
              (photo): photo is NonNullable<typeof photo> => photo !== null
            )

          const photosWithoutProjections = currentPhotos
            .filter(
              (photo) =>
                !photoResponses.some((pr) => pr.image_name === photo.filename)
            )
            .map((photo) => omit(photo, 'projection'))

          const allPhotos = [...updatedPhotos, ...photosWithoutProjections]

          // Set filtered photos based on updated photos
          const filteredPhotos = updatedPhotos.length > 0 ? updatedPhotos : null
          kv.setFilteredPhotos(filteredPhotos)

          return allPhotos as KVPhoto[]
        })
      } catch (error) {
        console.error(
          'Failed to fetch nearby photos for orbit position:',
          error
        )
        kv.setFilteredPhotos(null)
        // Use functional update for error case too
        kv.setPhotos(
          (currentPhotos: KVPhoto[]) =>
            currentPhotos.map((photo) => omit(photo, 'projection')) as KVPhoto[]
        )
      }
      kv.setFilteringPhotos(false)
    },
  } as KVMessageDef
}
